import { computed, ref, markRaw } from "vue";
import { defineStore, storeToRefs } from "pinia";
import { BarChart3, MessageCircle, File, FileText, FileType2 } from "lucide-vue-next";
import AiStar from "@/components/episto-ui/ai/AiStar.vue";
import { useSurveyStore } from "@stores/survey/survey";
import { isEmpty } from "@utils/toolBox";
import { useRoute } from "vue-router";

export const useSurveyDesignStore = defineStore("surveyDesign", () => {
  // imported stores
  const surveyStore = useSurveyStore();
  const { survey } = storeToRefs(surveyStore);

  const route = useRoute();
  const isCreatingSurvey = computed(() => route.name === "SurveyNew");

  // Survey Type (quantitative/qualitative)
  const getSurveyTypeOptions = ref([
    { value: "quantitative", label: "Quantitative", icon: markRaw(BarChart3) },
    { value: "qualitative", label: "Qualitative (AI chat)", icon: markRaw(MessageCircle) },
  ]);

  const getCurrentSurveyType = computed(() => {
    // ai_chat design type means qualitative
    if (survey.value?.design?.type === "ai_chat") return "qualitative";
    return "quantitative";
  });

  const updateSurveyType = (value) => {
    if (value === "qualitative") {
      // Set ai_chat design for qualitative
      survey.value.design = { type: "ai_chat", template_token: "ai_chat_fr" };
    } else {
      // Reset to custom for quantitative
      survey.value.design = {};
    }
  };

  const isQuantitative = computed(() => getCurrentSurveyType.value === "quantitative");
  const isQualitative = computed(() => getCurrentSurveyType.value === "qualitative");

  // Design (only for quantitative)
  const getCurrentDesign = computed(() => survey.value?.design?.type ?? "custom");
  const getDesignOptions = ref([
    { value: "custom", label: "Blank", icon: markRaw(File) },
    { value: "template", label: "Template", icon: markRaw(FileText) },
    { value: "word", label: "Word import", icon: markRaw(FileType2) },
    { value: "ai_powered", label: "AI prompt", icon: markRaw(AiStar) },
  ]);

  const updateDesign = (value) => {
    if (value === "ai_powered") survey.value.design = { type: "ai_powered" };
    else if (value === "word") survey.value.design = { type: "word", file: {} };
    else if (value === "template") survey.value.design = { type: "template", template_token: "external_fr" };
    else survey.value.design = {}; // Reset to default (custom)
  };

  // Getters
  const isAIDesign = computed(() => survey.value?.design?.type === "ai_powered");
  const isWordImportDesign = computed(() => survey.value?.design?.type === "word");
  const isTemplateDesign = computed(() => survey.value?.design?.type === "template");
  const isPromptEmpty = computed(
    () => getCurrentDesign.value === "ai_powered" && isEmpty(survey.value?.design?.prompt),
  );
  const isFileMissing = computed(() => getCurrentDesign.value === "word" && isEmpty(survey.value?.design?.file));
  const isTemplateMissing = computed(
    () => getCurrentDesign.value === "template" && isEmpty(survey.value?.design?.template_token),
  );

  // Prompt
  const getCurrentPrompt = computed(() => survey.value?.design?.prompt ?? "");
  const updatePrompt = (value) => (survey.value.design.prompt = value);

  // Template
  const getCurrentTemplate = computed(() => survey.value?.design?.template_token ?? "");
  const getTemplateOptions = ref([{ value: "external_fr", label: "External" }]);
  const updateTemplate = (value) => (survey.value.design.template_token = value);

  // File
  const getCurrentWordFile = computed(() => survey.value?.design?.file ?? {});
  const updateWordFile = (value) => (survey.value.design.file = value);

  return {
    // Survey Type
    getSurveyTypeOptions,
    getCurrentSurveyType,
    updateSurveyType,
    isQuantitative,
    isQualitative,
    // Design
    getCurrentDesign,
    getDesignOptions,
    updateDesign,
    // Getters
    isAIDesign,
    isWordImportDesign,
    isTemplateDesign,
    isPromptEmpty,
    isFileMissing,
    isTemplateMissing,
    // Prompt
    getCurrentPrompt,
    updatePrompt,
    // Template
    getCurrentTemplate,
    getTemplateOptions,
    updateTemplate,
    // File
    getCurrentWordFile,
    updateWordFile,
    // Route
    isCreatingSurvey,
  };
});
